import {
  type AdClip,
  type AdClipEvent,
  AdClipType,
  type AdSlot,
  type AdSlotEvent,
  AdSlotType,
  AdvertisingEvent,
  type PlayerInterface,
} from '@foundation-player/loader';
import { derived, get, type Readable, readonly, writable, type Writable } from 'svelte/store';
import type { PlayerStoreModule } from './player-store-types';

/**
 * Provides store values for many aspects of advertising.
 *
 * Most values can be derived from the current ad slot or the current ad clip.
 * That means, we mostly only have to set the ad slot at AdSlotStarted and the
 * ad clip at AdStarted (which should be named AdClipStarted), and almost
 * everything else gets derived automatically.
 */
export class AdvertisingPlayerStore
  implements
    PlayerStoreModule<
      | 'adClip'
      | 'adDuration'
      | 'adPlaying'
      | 'currentAdTime'
      | 'commercialPlaying'
      | 'indexOfCurrentCommercialAdClip'
      | 'interactiveAdPlaying'
      | 'linearAdPlaying'
      | 'numberOfCommercialAdClips'
    >
{
  /* Currently not needed in components, so we only use a private field. */
  readonly #adSlot: Writable<AdSlot | null> = writable(null);

  readonly #adClip: Writable<AdClip | null> = writable(null);
  readonly adClip = readonly(this.#adClip);

  /* Derived from the adSlot: When there is one, an ad is playing. */
  readonly adPlaying = derived(this.#adSlot, (adSlot) => adSlot != null);

  /* Derived from the adSlot: When there is one, and it's a linear one, a linear
   * ad is playing. */
  readonly linearAdPlaying = derived(this.#adSlot, (adSlot) => (adSlot ? this.#isLinearAdSlot(adSlot) : false));

  /* Derived from the adClip: When there is one and its type is commercial, a
   * commercial is playing */
  readonly commercialPlaying = derived(this.#adClip, (adClip) => (adClip ? this.#isCommercial(adClip) : false));

  /* Derived from the adClip: When there is one and its type is commercial, a
   * commercial is playing */
  readonly sponsoredAdPlaying = derived(this.#adClip, (adClip) =>
    adClip ? adClip.type === AdClipType.Sponsored : false,
  );

  /* Derived from the adClip: When there is one and it is interactive, an
   * interactive ad is playing */
  readonly interactiveAdPlaying = derived(this.#adClip, (adClip) => (adClip ? adClip.isInteractive : false));

  /* Derived from the adSlot: When there is one, we get the number of its
   * commercials */
  readonly numberOfCommercialAdClips = derived(this.#adSlot, (adSlot) => (adSlot ? adSlot.getCommercials() : 0));

  /* Not derived from the adSlot or adClip (though perhaps there is a way) */
  readonly #indexOfCurrentCommercialAdClip: Writable<number> = writable(0);
  readonly indexOfCurrentCommercialAdClip = readonly(this.#indexOfCurrentCommercialAdClip);

  /* Derived from the adSlot and the adClip. */
  readonly adDuration = derived([this.#adSlot, this.#adClip], ([adSlot, adClip]) => {
    if (!adSlot) {
      return 0;
    }

    // during an opener, bumper or closer, the UI should not be shown at all,
    // but since this has not been a product requirement, we at least want to
    // show the duration of the clip, not of the list of commercials
    if (adClip && !this.#isCommercial(adClip)) {
      return adClip.duration;
    }

    return adSlot.getDuration();
  });

  /* Derived from the adSlot and commercialPlaying (and only needed internally
   * for further calculations).
   * Using adSlot _and_ adClip as sources, the derived value will be
   * re-calculated for each new adClip, even though we don't need the adClip to
   * get the value.
   */
  readonly #completedAdTime = derived(
    [this.#adSlot, this.commercialPlaying, this.#adClip],
    ([adSlot, commercialPlaying, _adClip]) =>
      adSlot && commercialPlaying ? this.#getPlayedCommercialDuration(adSlot) : 0,
  );

  /* Derived from several values, but will be initialized in the constructor,
   * since one value comes from an external store. */
  readonly currentAdTime: Readable<number>;

  /* Derived from several values, but will be initialized in the constructor
   * after initialization of currentAdTime */
  readonly adSlotIsSkippable: Readable<boolean>;

  constructor(currentTime: Readable<number>) {
    /* Derived from the currentTime (which is the playhead of either content or
     * the current adClip), the completedAdTime and whether an ad is playing:
     * When an ad is playing, we add the currentTime to the completedAtTime.
     */
    this.currentAdTime = derived(
      [currentTime, this.#completedAdTime, this.adPlaying],
      ([currentTime, completedAdTime, adPlaying]) => (adPlaying ? currentTime + completedAdTime : 0),
    );

    /* Derived from the currentAdTime.
     * When an ad is playing, we continuously check if the ad can be skipped at that moment.
     * Whether and when the ad can be skipped is determined by the adSlot configuration.
     */
    this.adSlotIsSkippable = derived([this.#adSlot, this.currentAdTime], ([adSlot, currentAdTime]) =>
      adSlot ? adSlot.canSkip(currentAdTime) : true,
    );
  }

  initializeStateFromPlayer(player: PlayerInterface) {
    player.addEventListener(AdvertisingEvent.AdSlotStarted, (event: AdSlotEvent) => {
      // there is a great chance this never gets called for the pre-rolls,
      // because the ui gets created after the event got emitted already
      // that's why we also initialize the values with the function call
      this.#adSlot.set(event.adSlot);
      // we start with a value of 0, so that with the first AdStarted event it
      // gets incremented to 1
      this.#indexOfCurrentCommercialAdClip.set(0);
    });

    player.addEventListener(AdvertisingEvent.AdSlotCompleted, () => {
      this.#adSlot.set(null);
      this.#adClip.set(null);
      this.#indexOfCurrentCommercialAdClip.set(0);
    });

    player.addEventListener(AdvertisingEvent.AdStarted, (event: AdClipEvent) => {
      this.#adClip.set(event.adClip);

      const commercial = this.#isCommercial(event.adClip);

      if (commercial) {
        this.#indexOfCurrentCommercialAdClip.update((index) => index + 1);
      }
    });

    // Get the current adSlot in case the store gets initialized after the
    // adSlot started.
    const adSlot = player.getAdvertisingAdapter()?.getCurrentAdSlot();

    if (adSlot?.isActive()) {
      this.#adSlot.set(adSlot);
      this.#adClip.set(adSlot.getCurrentClip() ?? null);
      this.#indexOfCurrentCommercialAdClip.set(this.#getPlayedCommercialCount(adSlot));
    }
  }

  #isLinearAdSlot(adSlot: AdSlot): boolean {
    return adSlot.getType() !== AdSlotType.NonLinear;
  }

  #isCommercial(adClip: AdClip | undefined | null): boolean {
    const linearAdPlaying = get(this.linearAdPlaying);

    if (adClip) {
      // according to documentation the AdClipType of 'unknown' should be regarded as a commercial. We make
      // sure though that the UI only regards it as a commercial if a linear ad is actually playing.
      return adClip.type === AdClipType.Commercial || (adClip.type === AdClipType.Unknown && linearAdPlaying);
    } else {
      return false;
    }
  }

  #getPlayedCommercials(adSlot: AdSlot): AdClip[] {
    return adSlot.getPlayedClips().filter((adClip) => this.#isCommercial(adClip));
  }

  #getPlayedCommercialCount(adSlot: AdSlot | undefined): number {
    return adSlot ? this.#getPlayedCommercials(adSlot).length : 0;
  }

  #getPlayedCommercialDuration(adSlot: AdSlot): number {
    return this.#getPlayedCommercials(adSlot)
      .map((adClip) => adClip.duration)
      .reduce((a, b) => a + b, 0);
  }
}
